import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {Topic, Service, Action } from 'roslib'

import { useRos } from '../../services/Ros/utils/rosProvider';
import { IStopInfo, StopData } from '../../components/ZoneTravel/StopTravelBody';
import { useFetch } from '../../services/Requests/useFetch';
import { buildTopicOptions, publish } from '../../services/Ros/utils/rosTopicCommands';
import { useServiceCaller } from '../../services/Ros/ServiceCaller';
import { parseRobotState, RobotStateMessage } from '../../components/DrivingControls/CancelControl';
import { useConnection } from '../../services/Ros/Connection';
import { useMsg } from '../../services/Ros/Subscriber';

// Define the structure of OccupancyGrid info
interface OccupancyGridInfo {
  resolution: number;
  width: number;
  height: number;
  origin: {
    position: { x: number; y: number; z: number };
    orientation: { x: number; y: number; z: number; w: number }; // Add this
  };
}


// Define the structure of OccupancyGrid
interface OccupancyGrid {
  info: OccupancyGridInfo;
  data: number[];
}

export interface Marker {
  x: number;
  y: number;
  id?: string;
  zone?: number;
  stop?: number;
}

// Types for Transform and OccupancyGrid
interface Transform {
  x: number;
  y: number;
  z: number;
  rotation: {
    x: number;
    y: number;
    z: number;
    w: number;
  };
}

const options: RequestInit = {
  method: "GET"
}

// Props interface for the component
interface RobotMapOverlayProps {
  autoCenterOnRobot?: boolean; // Optional toggle for auto-centering
  markerList?:IStopInfo | null;
  handleMarker?: (selectedMarker: StopData) =>void ;
  navigating?: boolean;  // New prop for enabling navigation
  buttons?: boolean;
}

const xor = (a: any, b: any): boolean => {
  if ((a || b)&& !(a && b)){
    return true
  } 
  else{
    return false;
  }
  
};

const RobotMapOverlay: React.FC<RobotMapOverlayProps> = ({ autoCenterOnRobot = false,markerList = null, navigating = true, handleMarker,buttons = false }) => {
  const [robotPosition, setRobotPosition] = useState<Transform | null>(null);
  const [centerCamera, setCenterCamera] = useState<boolean>(autoCenterOnRobot)
  const [mapData, setMapData] = useState<OccupancyGrid | null>(null);
  const fetchMissionPlanData = useFetch<StopData[]>('/api/missions/get_stops_data', options);
  const { callService } = useServiceCaller();
  const connection = useConnection()
  const [parsedState, setParsedState] = useState<string>("UNKNOWN");
  const msg = useMsg();

  const [markers, setMarkers] = useState<IStopInfo| null>(markerList);

  try {
        useEffect(() => {
            const fetchRobotState = async () => {
                const request: any = {};
  
                    const result = await callService(request);
                    if (result.success && result.message) {
                        setParsedState(parseRobotState({data: parseInt(result.message)} as RobotStateMessage))
                        console.log(result)
                    } else {
                        console.error("Service returned an unsuccessful response.");
                    }
            };
            if (connection){
              fetchRobotState();
            }
        }, [callService,connection]);
    } catch (error) {
        console.error("Service call failed:", error);
    }
  
    useEffect(() => {
        setParsedState(parseRobotState(msg as RobotStateMessage))
    }, [msg]);
  

  useEffect(() => {
    fetchMissionPlanData.get();

    return () => {
        fetchMissionPlanData.abortRequest && fetchMissionPlanData.abortRequest();
    }
  }, []);

  useEffect(() => {
      const { state: { status, data } } = fetchMissionPlanData;

      if (status === 'fetched' && data && data.length > 0) {
          // console.log(data)
          setMarkers({ stops: data });
      }
  }, [fetchMissionPlanData.state]);

  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ros = useRos();
  const ref = useRef<HTMLDivElement | null>(null); // Specify the type for the ref
  const [canvasWidth, setCanvasWidth] = useState<number>(407);
  const [canvasHeight, setCanvasHeight] = useState<number>(285);
  const [tileRes, setTileRes] = useState<number>(128)
  const zoomLevels = [64, 128, 256, 512, 1024];
  const [isRightClicking, setIsRightClicking] = useState(false);
  const [disabled,setDisabled] = useState(true)
  const [initialClickPos, setInitialClickPos] = useState<{ x: number; y: number } | null>(null);
  const [targetOrientation, setTargetOrientation] = useState<number>(0);
  const [lastZoomTime, setLastZoomTime] = useState<number>(0);
  const ZOOM_COOLDOWN = 100; // milliseconds
  const default_resolution = 0.05


  const ROS = useRos();
  const [pathNameTopic, setPathNameTopic] = useState<Topic<any> | null>(null);
  const [goToGoalService, setGoToGoalService] = useState<Service<any,any> | null>(null);

  function eulerToQuaternion(roll: number, pitch: number, yaw: number): { x: number; y: number; z: number; w: number } {
    const cy = Math.cos(yaw * 0.5);
    const sy = Math.sin(yaw * 0.5);
    const cp = Math.cos(pitch * 0.5);
    const sp = Math.sin(pitch * 0.5);
    const cr = Math.cos(roll * 0.5);
    const sr = Math.sin(roll * 0.5);
  
    return {
      w: cr * cp * cy + sr * sp * sy,
      x: sr * cp * cy - cr * sp * sy,
      y: cr * sp * cy + sr * cp * sy,
      z: cr * cp * sy - sr * sp * cy
    };
  }
  
  function getRotationQuaternion(degrees: number): { x: number; y: number; z: number; w: number } {
    const radians = (degrees * Math.PI) / 180;
    return eulerToQuaternion(0, 0, radians);
  }
  

  useEffect(() => {
      // Set up the path_name topic publisher
      const topicOptions = buildTopicOptions(ROS, '/path_name', 'std_msgs/String');
      const rosTopic = new Topic(topicOptions);
      rosTopic.advertise();
      setPathNameTopic(rosTopic);
      setDisabled(true)

      // Set up the go_to_goal service
      const rosService = new Service({
          ros: ROS,
          name: '/go_to_goal',
          serviceType: 'std_srvs/Trigger'
      });
      setGoToGoalService(rosService);

      // Cleanup on unmount
      return () => {
          if (rosTopic) {
              rosTopic.unadvertise();
          }
          if (rosService) {
              setGoToGoalService(null);
          }
      };
  }, [ROS]);

  const publishPathName = (stop: string) => {
      if (pathNameTopic) {
          console.log(stop)
          const msg: any = { data: stop };
          publish(pathNameTopic, msg);
      }
  };
  

  const callGoToGoalService = async () => {
      return new Promise((resolve, reject) => {
          if (goToGoalService) {
              goToGoalService.callService({}, (result:any) => {
                  resolve(result);
              }, (error:any) => {
                  reject(error);
              });
          } else {
              reject(new Error('Service not available'));
          }
      });
  }

  useLayoutEffect(() => {
    const updateCanvasSize = () => {
      if (ref.current) {
        setCanvasWidth(ref.current.offsetWidth);
        setCanvasHeight(ref.current.offsetHeight);
      }
      
    };
  
    // Set initial size
    updateCanvasSize();
  
    // Handle window resize
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);
  
  // Panning and zooming states
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [newMap, setnewMap] = useState<boolean>(true)

  const [baseDim, setBaseDim] = useState({ x: canvasWidth, y: canvasHeight, resolution:  default_resolution,x0: -10.2, y0: -4.64});
  // setOffset({x:((baseDim.x0)/baseDim.resolution)* scale + offset.x,y:(-baseDim.y*scale-(baseDim.y0*scale/baseDim.resolution))})
  // console.log(baseDim)
  // Cached image to prevent constant rerendering
  const mapImageRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (markerList){
      setMarkers(markerList)
      console.log(markerList)
    }

  }, [markerList]);

  // useEffect(()=> {
  //   console.log(`Offset Changed ${offset.x},${offset.y}`)
  // },[offset])

  useEffect(() => {
    // Subscribe to the '/map' topic
    const mapTopic = new Topic({
      ros: ros,
      name: 'global_costmap/map',
      messageType: 'nav_msgs/OccupancyGrid',
      throttle_rate: 1000
    });

    // Subscribe to the TF topic
    const tfTopic = new Topic({
      ros: ros,
      name: '/robot_position',
      messageType: 'tf2_msgs/TFMessage',
      throttle_rate:500
    });


    // Extract the transform for base_link relative to map
    tfTopic.subscribe((message: any) => {
      // console.log(message)
      const transforms = message.transforms;
      for (const transform of transforms) {
        if (
          transform.child_frame_id === 'base_link' &&
          transform.header.frame_id === 'map'
        ) {
          const { translation, rotation } = transform.transform;
          // console.log(translation.x,translation.y,rotation)
          setRobotPosition({
            x: translation.x,
            y: translation.y,
            z: translation.z,
            rotation: rotation,
          });
        }else{
          // console.log(message)
        }
      }
    });

    // mapTopic.subscribe((message: ROSLIB.Message) => {
    //   // console.log("Map Message")
    //   if (newMap){
    //     const grid = message as OccupancyGrid; // Cast message to OccupancyGrid


    //     setMapData(grid);
    //     console.log(grid.info)
    //     setnewMap(false)
    //     mapTopic.unsubscribe()
    //   }
    // });
    type TransformOptions = {
      rotateDegrees?: 0 | 90 | 180 | 270;
      flipHorizontal?: boolean;
      flipVertical?: boolean;
    };
    
    function transformOccupancyGrid(
      data: number[],
      width: number,
      height: number,
      options: TransformOptions = {}
    ): { data: number[]; width: number; height: number } {
      const rotate = options.rotateDegrees ?? 0;
      const flipX = options.flipHorizontal ?? false;
      const flipY = options.flipVertical ?? false;
    
      let newWidth = width;
      let newHeight = height;
    
      if (rotate === 90 || rotate === 270) {
        newWidth = height;
        newHeight = width;
      }
    
      const transformed = new Array<number>(width * height);
    
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          let srcX = x;
          let srcY = y;
    
          // Apply flip first
          if (flipX) srcX = width - srcX - 1;
          if (flipY) srcY = height - srcY - 1;
    
          // Apply rotation
          let dstX: number, dstY: number;
    
          switch (rotate) {
            case 0:
              dstX = srcX;
              dstY = srcY;
              break;
            case 90:
              dstX = height - srcY - 1;
              dstY = srcX;
              break;
            case 180:
              dstX = width - srcX - 1;
              dstY = height - srcY - 1;
              break;
            case 270:
              dstX = srcY;
              dstY = width - srcX - 1;
              break;
            default:
              throw new Error("Invalid rotation angle. Must be 0, 90, 180, or 270.");
          }
    
          const srcIndex = y * width + x;
          const dstIndex = dstY * newWidth + dstX;
          transformed[dstIndex] = data[srcIndex];
        }
      }
    
      return {
        data: transformed,
        width: newWidth,
        height: newHeight
      };
    }
    

    mapTopic.subscribe((message: any) => {
      if (newMap) {
        const grid = message as OccupancyGrid;
    
        const { data, info } = grid;
        const resolution = grid.info.resolution;
        const origin = grid.info.origin;
    
        // Example: Rotate 90° clockwise and flip horizontally
        const transformed = transformOccupancyGrid(data, info.width, info.height, {
          rotateDegrees: 180,
          flipHorizontal: true
        });
    
        const newOrientation = getRotationQuaternion(180);
    
        const newGrid: OccupancyGrid = {
          info: {
            resolution,
            width: transformed.width,
            height: transformed.height,
            origin: {
              position: {
                x:origin.position.x,
                y:origin.position.y,
                z:origin.position.z},
              orientation: newOrientation
            }
          },
          data: transformed.data
        };
    
        setMapData(newGrid);
        setnewMap(false);
        mapTopic.unsubscribe();
      }
    });
    

    // Cleanup the subscription when the component is unmounted
    return () => {
      mapTopic.unsubscribe();
      tfTopic.unsubscribe();
    };
  }, [ros]);

  const zoomIn = () => {
    const currentIndex = zoomLevels.indexOf(tileRes);
    if (currentIndex < zoomLevels.length - 1) {
      const newZoom = zoomLevels[currentIndex + 1];
      const newScale = scale * newZoom/tileRes
      // console.log(newScale)
      setScale(newScale)
      setTileRes(newZoom);
      if (centerCamera && robotPosition) {
        const centeredOffsetX = (canvasWidth/2)+(((-(robotPosition.x-baseDim.x0)*newScale)/baseDim.resolution));
        const centeredOffsetY = (canvasHeight/2)+(-baseDim.y*newScale  + ((robotPosition.y-baseDim.y0)*newScale)/baseDim.resolution);

        setOffset(limitOffset(centeredOffsetX, centeredOffsetY));
      }
      return newScale
    }
    return scale
  };
  
  const zoomOut = () => {
    const currentIndex = zoomLevels.indexOf(tileRes);
    if (currentIndex > 0) {
      const newZoom = zoomLevels[currentIndex - 1];
      const newScale = scale * newZoom/tileRes
      // console.log(newScale)
      setScale(newScale)
      setTileRes(newZoom);
      if (centerCamera && robotPosition) {

        const centeredOffsetX = (canvasWidth/2)+(((-(-robotPosition.x-baseDim.x0)*newScale)/baseDim.resolution));
        const centeredOffsetY = (canvasHeight/2)+(-baseDim.y*newScale  + ((-robotPosition.y-baseDim.y0)*newScale)/baseDim.resolution);

        setOffset(limitOffset(centeredOffsetX, centeredOffsetY));
      }
      return newScale
    }
    return scale
  };

  // Helper function to convert quaternion to yaw (rotation in 2D plane)
  const getYawFromQuaternion = (q: { x: number; y: number; z: number; w: number }) => {
    const siny_cosp = 2 * (q.w * q.z + q.x * q.y);
    const cosy_cosp = 1 - 2 * (q.y * q.y + q.z * q.z);
    return Math.atan2(siny_cosp, cosy_cosp) - (Math.PI/2); // Yaw angle in radians
  };

  // Function to limit the offset for panning
  const limitOffset = (x: number, y: number, nScale: number | null = null) => {
    let newX= x;
    let newY= y;
    if (nScale){
      console.log()
    }
    return { x: newX, y: newY };
  };

  // Function to center the canvas on the robot's position
  const centerOnRobot = (robotX: number, robotY: number) => {
    if (centerCamera) {
      const centeredOffsetX = (canvasWidth/2)+(((-(robotX-baseDim.x0)*scale)/baseDim.resolution));
      const centeredOffsetY = (canvasHeight/2)+(-baseDim.y*scale  + ((robotY-baseDim.y0)*scale)/baseDim.resolution);
      setOffset(limitOffset(centeredOffsetX, centeredOffsetY));
    }
  };

  const renderTile = (
    ctx: CanvasRenderingContext2D,
    tileX: number,
    tileY: number,
    startIndexX: number,
    startIndexY: number,
    tileSize: number,
  ) => {
    // Create an off-screen canvas for the tile
    const tileCanvas = document.createElement('canvas');
    const tileCtx = tileCanvas.getContext('2d');
  
    if (!tileCtx) return;
  
    // Set the size of the off-screen canvas
    tileCanvas.width = tileSize;
    tileCanvas.height = tileSize;
  
    // Create an ImageData object for the tile
    const imgData = tileCtx.createImageData(tileSize, tileSize);
  
    if (mapData) {
      const { data, info } = mapData;
      const { width, height } = info;
  
      for (let y = 0; y < tileSize; y++) {
        for (let x = 0; x < tileSize; x++) {
          const dataX = startIndexX + x;
          const dataY = startIndexY + y;
  
          // Ensure within map bounds
          if (dataX >= 0 && dataX < width && dataY >= 0 && dataY < height) {
            const index = dataY * width + dataX;
            const value = data[index];
  
            // Determine color based on occupancy grid value
            const color = value === 0 ? 255 : value === 100 ? 0 : 128; // Free=White, Occupied=Black, Unknown=Gray
  
            // Calculate pixel position in ImageData array
            const pixelIndex = (y * tileSize + x) * 4;
  
            // Set RGB channels to the calculated color
            imgData.data[pixelIndex + 0] = color; // Red
            imgData.data[pixelIndex + 1] = color; // Green
            imgData.data[pixelIndex + 2] = color; // Blue
            imgData.data[pixelIndex + 3] = 255;   // Alpha (fully opaque)
          } else {
            // Out of bounds, fill with gray (empty area)
            const pixelIndex = (y * tileSize + x) * 4;
            imgData.data[pixelIndex + 0] = 128; // Red
            imgData.data[pixelIndex + 1] = 128; // Green
            imgData.data[pixelIndex + 2] = 128; // Blue
            imgData.data[pixelIndex + 3] = 255; // Alpha
          }
        }
      }
    } else {
      // No map data, render tile as a gray square
      for (let y = 0; y < tileSize; y++) {
        for (let x = 0; x < tileSize; x++) {
          const pixelIndex = (y * tileSize + x) * 4;
          imgData.data[pixelIndex + 0] = 128; // Red
          imgData.data[pixelIndex + 1] = 128; // Green
          imgData.data[pixelIndex + 2] = 128; // Blue
          imgData.data[pixelIndex + 3] = 255; // Alpha
        }
      }
    }
  
    // Place the generated image data onto the off-screen canvas
    tileCtx.putImageData(imgData, 0, 0);
  
    // Scale and draw the tile on the main canvas
    ctx.drawImage(
      tileCanvas, 
      0, 0, tileSize, tileSize,         // Source dimensions
      tileX, tileY, tileSize * scale, tileSize * scale // Destination with scaling
    );
  };

  const renderMap = (
    ctx: CanvasRenderingContext2D, 
  ) => {
    if (!mapData) return;
  
  
    // Calculate the visible tile range based on the canvas dimensions and offset
    const startX = Math.floor(-offset.x / (tileRes * scale)); // Left-most visible tile
    const startY = Math.floor(-offset.y / (tileRes * scale)); // Top-most visible tile
    const endX = Math.ceil((canvasWidth - offset.x) / (tileRes * scale)); // Right-most visible tile
    const endY = Math.ceil((canvasHeight - offset.y) / (tileRes * scale)); // Bottom-most visible tile
  
    // Loop over the visible tile range
    for (let x = startX; x <= endX; x++) {
      for (let y = startY; y <= endY; y++) {
        const tileX = x * tileRes * scale + offset.x; // Actual X position on the canvas
        const tileY = y * tileRes * scale + offset.y; // Actual Y position on the canvas
        const startIndexX = x * tileRes; // X index in the data grid
        const startIndexY = y * tileRes; // Y index in the data grid
  
        // Render each visible tile
        renderTile(ctx, tileX, tileY, startIndexX, startIndexY, tileRes);
      }
    }

    ctx.restore();

  };
  
  // Render the robot on top of the map
  const renderRobot = (ctx: CanvasRenderingContext2D) => {
    if (robotPosition && mapData) {
      const robotX =  ((robotPosition.x-baseDim.x0)* scale)/baseDim.resolution + offset.x; // Adjust for scaling and offset
      const robotY = (baseDim.y*scale  - ((robotPosition.y-baseDim.y0)*scale)/baseDim.resolution)+offset.y;
      // console.log(offset.y,robotX,robotY)
      const yaw = getYawFromQuaternion(robotPosition.rotation);
      const myaw = 0//getYawFromQuaternion(mapData.info.origin.orientation);


      // Draw the robot as a triangle to show orientation
      ctx.save();
      ctx.translate(robotX, robotY); // Move to robot position
      ctx.rotate(-yaw+myaw); // Rotate based on yaw (negative to adjust canvas rotation direction)

      // Draw the triangle representing the robot
      ctx.beginPath();
      ctx.moveTo(0, 0); // Point of the triangle (front of the robot)
      ctx.lineTo(-8, 18);  // Left corner
      ctx.lineTo(8, 18);   // Right corner
      ctx.closePath();
      ctx.fillStyle = 'blue';
      ctx.fill();
      ctx.restore();
    }
  };

  const renderMarkers = (ctx: CanvasRenderingContext2D) => {
    if (markers && mapData){
      // const res = mapData.info.resolution
      markers.stops.forEach(marker => {
        // Calculate the marker's position on the canvas
        const markerX = (((marker.gps[0]-baseDim.x0)*scale)/baseDim.resolution) + offset.x;
        const markerY = (baseDim.y*scale-((marker.gps[1]-baseDim.y0)*scale)/baseDim.resolution)+offset.y;

        // Define the radius of the marker head
        const radius = 10 ; // Adjust scale as needed

        // Draw the pin body (a triangle pointing to the markerX, markerY)
        ctx.beginPath();
        ctx.moveTo(markerX - radius / 2, markerY - radius * 2);  // Left side of the triangle
        ctx.lineTo(markerX + radius / 2, markerY - radius * 2);  // Right side of the triangle
        ctx.lineTo(markerX, markerY);  // Point of the triangle at the origin (markerX, markerY)
        ctx.closePath();
        ctx.fillStyle = marker.stop.toLowerCase()==='home' ?'green' : 'red'; // Match the fill color to the circle
        ctx.fill();
        ctx.stroke();

        // Draw the marker circle (the head of the pin)
        ctx.beginPath();
        ctx.arc(markerX, markerY - radius * 2, radius, 0, 2 * Math.PI); // Circle center is above the point
        ctx.fillStyle = marker.stop.toLowerCase()==='home' ?'green' : 'red'; // Color of the marker
        ctx.fill();
        ctx.stroke();

      });
    }
  };
  

    // Performance optimized render with requestAnimationFrame
    useEffect(() => {
      if (centerCamera && robotPosition){
        centerOnRobot(robotPosition.x, robotPosition.y);
      }
    }, [robotPosition]);

  // Performance optimized render with requestAnimationFrame
  useEffect(() => {

    const render = () => {
      const canvas = canvasRef.current;
      const mapCanvas = mapImageRef.current;
      if (canvas && mapCanvas) {
        const ctx = canvas.getContext('2d');
        if (ctx && mapData) {
          // Clear the canvas before redrawing
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // const { info } = mapData;
          // const { origin } = info;

          // const yaw = getYawFromQuaternion(origin.orientation);

          ctx.save();
          // const originX = 0
          // // const originY = baseDim.y
          // const originX =  ((-baseDim.x0)* scale)/baseDim.resolution + offset.x; // Adjust for scaling and offset
          // const originY = (baseDim.y*scale  - ((-baseDim.y0)*scale)/baseDim.resolution)+offset.y;
          // // Step 1: Move canvas origin to the map's origin in pixel space
          // const originX = -origin.position.x*scale/resolution;
          // const originY = - (info.height*scale-((-origin.position.y)*scale)/resolution);
          // console.log(originX,originY,yaw);
          // ctx.translate(originX,originY);

          // // // Step 2: Apply rotation around origin
          // ctx.rotate(-yaw); // Negative because canvas rotates clockwise
        
          // // // // Step 3: Translate again to move drawing back to (0, 0)
          // ctx.translate(-originX, -originY);
          
          // // Draw the cached map first
          // ctx.drawImage(mapCanvas, 0, 0, baseDim.x, baseDim.y, offset.x, offset.y, baseDim.x * scale, baseDim.y * scale);
          // Render the map as tiles
          renderMap(ctx);

          renderMarkers(ctx);

          if (isRightClicking && parsedState === "READY"){
            drawPoseIndicator(ctx)
          }

          // Then render the robot on top
          renderRobot(ctx);
          // Restore context so UI overlays don't rotate
          ctx.restore();
        }
      }

      // Request the next animation frame
      // animationFrameId = requestAnimationFrame(render);
    };

    render(); // Start the render loop

    // return () => {
    //   cancelAnimationFrame(animationFrameId); // Cleanup on unmount
    // };
    // robotPosition,markers, scale, offset,isRightClicking,targetOrientation
  }, [robotPosition,markers, scale, offset,isRightClicking,targetOrientation]);

  // Cache the map image the first time it's drawn and only update when mapData changes
  useEffect(() => {
    if (mapData) {
      const { width, height, resolution,origin } = mapData.info;
    
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      if (tempCtx) {
        tempCanvas.width = width;
        tempCanvas.height = height;
        

        const imgData = tempCtx.createImageData(width, height);
        setBaseDim({ x: width, y: height, resolution: resolution,x0:origin.position.x,y0:origin.position.y });
        // console.log(baseDim)

        for (let i = 0; i < mapData.data.length; i++) {
          const value = mapData.data[i];

          const color = value === 0 ? 255 : value === 100 ? 0 : 128; // Free=White, Occupied=Black, Unknown=Gray
          imgData.data[i * 4 + 0] = color; // Red
          imgData.data[i * 4 + 1] = color; // Green
          imgData.data[i * 4 + 2] = color; // Blue
          imgData.data[i * 4 + 3] = 255;   // Alpha
        }

        tempCtx.putImageData(imgData, 0, 0);
        mapImageRef.current = tempCanvas;
      }
    }
  }, [mapData]);

  const drawPoseIndicator = (ctx: CanvasRenderingContext2D) => {
    if (initialClickPos && canvasRef.current) {
      // console.log("Pose:", initialClickPos, "Orientation:", targetOrientation);
      const { left, top } = canvasRef.current.getBoundingClientRect()
  
      // Adjust initial position with resolution, scaling, and offset
      const posX = (initialClickPos.x - left);
      const posY = (initialClickPos.y - top);
      
      // console.log(initialClickPos,posX,posY,scale,offset)
      ctx.restore();
      // Draw the initial position as a blue circle
      // Draw the robot as a triangle to show orientation
      ctx.save();
      ctx.translate(posX, posY); // Move to robot position
      ctx.rotate(-targetOrientation); // Rotate based on yaw (negative to adjust canvas rotation direction)

      // Draw the triangle representing the robot
      ctx.beginPath();
      ctx.moveTo(0, 0); // Point of the triangle (front of the robot)
      ctx.lineTo(-8, 18);  // Left corner
      ctx.lineTo(8, 18);   // Right corner
      ctx.closePath();
      ctx.fillStyle = 'green';
      ctx.fill();
      ctx.restore();
    }
  };
  
  

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 2 && canvasRef.current) {
      setIsRightClicking(true);
      // const { left, top } = canvasRef.current.getBoundingClientRect()
      const x1 = (e.clientX)
      const y1 = (e.clientY) 
      if (parsedState === "READY"){
        setInitialClickPos({ x: x1 ,y: y1 });
      }
      // console.log({ x: (e.clientX - left - offset.x), y: (e.clientY - top - offset.y) / scale },{clickX:e.clientX,clickY:e.clientY})
    }else{
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
    
  };

  const handleNavigationSet  = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (isRightClicking && initialClickPos && canvasRef.current && mapData && parsedState=="READY") {
      console.log(event)
      // const res = mapData.info.resolution
      // const { left, top } = canvasRef.current.getBoundingClientRect();
      // const clickX = (event.clientX - left - offset.x )+baseDim.x0+offset.x ;
      // const clickY = (event.clientY - top  - offset.y)-baseDim.y0+offset.y ;
      // const transX = ((clickX-offset.x)*res)/scale+baseDim.x0;
      // const transY = ((clickY+baseDim.y-offset.y)*res)/scale+baseDim.y0;
      // console.log(clickY,transY,baseDim.y0,(clickY*res)/scale,(offset.y*res)/scale)
      // console.log(clickX,transX,baseDim.x0,(clickX*res)/scale,(offset.x*res)/scale)

      const { left, top } = canvasRef.current.getBoundingClientRect();
      const clickX = (initialClickPos.x - left);
      const clickY = (initialClickPos.y - top) ;
      // const transX = ((clickX)*baseDim.resolution)+baseDim.x0;
      // const transY = ((clickY)*baseDim.resolution)+baseDim.y0;
      const transX = ((clickX - offset.x)*baseDim.resolution/scale) + baseDim.x0
      const transY = baseDim.y0-((clickY-offset.y-baseDim.y*scale)*baseDim.resolution/scale)
      console.log(clickY*baseDim.resolution,transY,baseDim.y0)
      console.log(clickX*baseDim.resolution,transX,baseDim.x0)

      // // Set up the ROS action client and goal
      // const actionClient = new ActionClient({
      //   ros: ros,
      //   serverName: '/navigate_to_pose',
      //   actionName: 'nav2_msgs/NavigateToPose',
      // });

      const action = new Action({
        ros: ros,
        name: '/navigate_to_pose',
        actionType: 'nav2_msgs/action/NavigateToPose',
      });

      // const orientationQuaternion = {
      //   x: 0,
      //   y: 0,
      //   z: Math.sin(targetOrientation / 2),
      //   w: Math.cos(targetOrientation / 2),
      // };
      // (degrees * Math.PI) / 180
      const orientationQuaternion = getRotationQuaternion(((targetOrientation*180)/Math.PI)+90)
      const now = Date.now();
      const secs = Math.floor(now / 1000);
      const nsecs = (now % 1000) * 1e6;

      const goal = {
          pose: {
            header: {
              frame_id: 'map',
              stamp: {
                secs: secs,
                nsecs: nsecs,
              },
            },
            pose: {
              position: { x: transX, y: transY, z: 0 },
              orientation: orientationQuaternion,
            },
          }
        };

      // Send the goal with optional feedback rate in ms
      action.sendGoal(goal,(result: any) => console.log('Result:', result));

      // goal.on('feedback', (feedback: any) => console.log('Feedback:', feedback));
      // goal.on('result', (result: any) => console.log('Result:', result));
      // goal.on('timeout', () => console.log('Goal canceled'));
      console.log(goal)
      setIsRightClicking(false);
      setInitialClickPos(null);
    }
  }

  const handleMouseMove = (e: MouseEvent | React.MouseEvent<HTMLCanvasElement>) => {
    // console.log(isRightClicking, initialClickPos)
    if (isRightClicking && initialClickPos && canvasRef.current && parsedState === "READY") {
      // const { left, top } = canvasRef.current.getBoundingClientRect()
      const x1 = e.clientX
      const y1 = e.clientY

      const dx = initialClickPos.x - x1;
      const dy = initialClickPos.y - y1;
      const angle = Math.atan2(dx, dy);
      // console.log(angle,x1,initialClickPos.x ,dx, offset.x, left)
      // console.log(angle,y1,initialClickPos.y,dy,offset.y,top)
      setTargetOrientation(angle);
    }else if (isDragging && xor(centerCamera,robotPosition)) {
      const newX = offset.x + e.clientX - dragStart.x;
      const newY = offset.y + e.clientY - dragStart.y;
      setOffset(limitOffset(newX, newY));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheelZoom = (event: React.WheelEvent) => {
    // console.log("Zooming")
    const now = Date.now();
    if (now - lastZoomTime < ZOOM_COOLDOWN) return;
    setLastZoomTime(now);
    let newScale = scale
    if (event.deltaY < 0) {
      newScale = zoomIn();
    } else {
      newScale = zoomOut();
    }
    // Calculate the zoom focal point (mouse position relative to the canvas)
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect &&  xor(centerCamera,robotPosition)) {
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      // console.log(event.clientX , event.clientY)
      // console.log(mouseX , mouseY)
      // console.log(scale,newScale)

      // Calculate offset adjustments based on the zoom change
      const dx = (mouseX - offset.x) * (newScale - scale) / scale;
      const dy = (mouseY - offset.y) * (newScale - scale) / scale;

      const newOffset = limitOffset(offset.x - dx, offset.y - dy, newScale);
      setOffset(newOffset);
    }
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (disabled){
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!markers) return;
    if (!navigating){
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const clickX = (event.clientX - rect.left - offset.x) / scale;
        const clickY = (event.clientY - rect.top - offset.y) / scale;
        console.log(clickX,clickY)

      }
    }
  
    // Get the mouse click position relative to the canvas
    const rect = canvas.getBoundingClientRect();
    const clickX = (event.clientX - rect.left);
    const clickY = (event.clientY - rect.top);
    // console.log(offset.x +clickX,offset.y +clickY)
    if(mapData){
      // Check if the click is within any marker
      const res = mapData.info.resolution
      markers.stops.forEach(marker => {
        const markerX = ((marker.gps[0]-mapData.info.origin.position.x)/res)* scale + offset.x; // Adjust for scaling and offset
        // const markerY = (baseDim.y -(mapData.info.origin.position.y/mapData.info.resolution))* scale + (-(marker.gps[1]/mapData.info.resolution)* scale - offset.y); // Invert Y and adjust for scaling
        const markerY = ((marker.gps[1]/res)-(mapData.info.origin.position.y/res))*scale+offset.y
    
        // Calculate the distance between the click and the marker
        const distance = Math.sqrt((clickX - markerX) ** 2 + (clickY - markerY) ** 2);
        // console.log(clickX,clickY,markerX,markerY)
        if (distance < 10 * scale) { // 10 * scale is the clickable area radius
          console.log(`Marker ${marker.zone} /${marker.stop} clicked!`);
           // Publish marker.stop to the '/path_name' topic
           publishPathName(marker.stop);

           // Call the service '/go_to_goal'
           try {
               const result = callGoToGoalService();
               console.log('Service called successfully:', result);
           } catch (error) {
               console.error('Service call failed:', error);
           }

          if (handleMarker){
            handleMarker(marker)
          }
          // You can trigger any event or callback here
        }
      });
    }
  };
  

  // Add event listeners for panning
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, offset]);

  return (
    <>
      <div ref={ref} style={{ height: '100%' }}>
        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          onMouseDown={handleMouseDown}
          onMouseUp={handleNavigationSet}
          onMouseMove={handleMouseMove}
          onWheel={handleWheelZoom}
          onClick={handleCanvasClick}
          onContextMenu={(e) => e.preventDefault()}
          style={{ cursor: navigating ? 'crosshair' : 'default' }}
        />
      </div>
      {buttons &&
      <div style={{ position: 'absolute', bottom: 10, left: 10, zIndex: 10 }}>
        
        <button onClick={() => {
          const newScale = zoomIn();
          const newOffset = limitOffset(offset.x, offset.y, newScale);
          setOffset(newOffset);
        }}>+</button>
        <br></br>
        <button onClick={() => {
          const newScale = zoomOut();
          const newOffset = limitOffset(offset.x, offset.y, newScale);
          setOffset(newOffset);
        }}>−</button>
      </div>}
      {buttons &&
      <div style={{ position: 'absolute', bottom: 10, right: 10, zIndex: 10 }}>
        <button onClick={() => {
          setCenterCamera(!centerCamera)
        }}>{centerCamera ? 'ꗃ' : '🔒︎'  }</button>
        
      </div>}
    </>
  );
};

export default RobotMapOverlay;
